// CatalogueDrawer — replaces SkuDetailPane.jsx for the catalogue context.
//
// Three tabs:
//   Details — every field on forecaster_catalog (existing + new ones
//             from migration 85: barcode, barcode_path, reorder_point,
//             list_price, tags). Includes the legacy unit_cost edit
//             which auto-appends to forecaster_price_history.
//   History — merged forecaster_price_history + forecaster_activity
//             (filtered by SKU name) into one timeline.
//   Linked  — Open POs only. Top customers dropped from MVP per Mo
//             (no customer dim on weekly_sales).
//
// Retire/Reactivate is admin-only and lives in the footer of the
// Details tab, replacing the legacy `confirm()` with an inline
// confirm strip.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { storage } from '../lib/awsStorage';
import SupplierPicker from './SupplierPicker';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const SANS  = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

// Migration 101 split — UoM is the packaging vocabulary, Category is
// the brand bucket. Keep these in sync with Catalogue.jsx.
const UOM_OPTIONS = ['Sachet Boxes', 'Pouches', 'Tins', 'Bottles', 'Jars', 'Boxes', 'Rewind', 'Accessories', 'Raw Materials'];
const CATEGORY_OPTIONS = ['', 'OneFit', 'OneLife', 'OneFitUS', 'OneCreatine', 'CocoFit', 'Other'];
const TAG_SUGGESTIONS = ['core', 'new', 'seasonal', 'bulk', 'fragile', 'fast-mover'];

function fmtMoney(n) { return `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtShortDate(ts) { if (!ts) return '—'; return new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtRelative(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m || '<1'}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return fmtShortDate(ts);
}

export default function CatalogueDrawer({
  session, item, allSkus = [], incomingData = [],
  onClose, onSaved,
}) {
  const isAdmin = session?.role === 'admin';
  // Catalogue edit (the form in DetailsTab) follows the same gate as
  // the "+ New SKU" button in Catalogue.jsx — admin role OR users with
  // the stock_write permission flag (default true; toggle off in Admin
  // Panel → user → Special).
  const canCatalogueWrite = isAdmin || session?.permissions?.stock_write !== false;
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState({});
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // The matching forecaster_skus row (1:1 with catalog) — needed for
  // run-rate-based reorder-point suggestions.
  const skuRow = useMemo(
    () => allSkus.find(s => s.catalog_id === item?.id) || null,
    [allSkus, item?.id],
  );

  const monthlyAvg = Number(skuRow?.monthly_avg_override ?? skuRow?.monthly_sales_avg ?? 0);
  const reorderSuggestion = useMemo(() => {
    const lead = Number(form.lead_time_days || item?.lead_time_days || 84);
    if (monthlyAvg <= 0) return null;
    return Math.ceil((monthlyAvg / 30) * lead * 1.2);
  }, [monthlyAvg, form.lead_time_days, item?.lead_time_days]);

  // Reset form when item changes.
  useEffect(() => {
    if (!item) return;
    setTab('details');
    setForm({
      sku_name: item.sku_name || '',
      sku_code: item.sku_code || '',
      uom: item.uom || 'Sachet Boxes',
      category: item.category || '',
      supplier_id: item.supplier_id || null,
      supplier_name: item.supplier_name || '',
      unit_cost: item.unit_cost ?? '',
      list_price: item.list_price ?? '',
      lead_time_days: item.lead_time_days ?? 84,
      batch_size: item.batch_size ?? 857,
      reorder_point: item.reorder_point ?? '',
      barcode: item.barcode || '',
      barcode_path: item.barcode_path || '',
      tags: Array.isArray(item.tags) ? item.tags.slice() : [],
      notes: item.notes || '',
    });
    setConfirmRetire(false);
    setJustSaved(false);
  }, [item?.id]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!item) return null;

  const margin = (() => {
    const cost = parseFloat(form.unit_cost);
    const price = parseFloat(form.list_price);
    if (!Number.isFinite(cost) || !Number.isFinite(price) || price <= 0) return null;
    return Math.round(((price - cost) / price) * 100);
  })();

  // What changed vs the catalog row?
  const dirtyFields = useMemo(() => {
    const out = [];
    const compare = (k, transform) => {
      const a = transform ? transform(form[k]) : form[k];
      const b = transform ? transform(item[k]) : item[k];
      const aN = a === '' || a == null ? null : a;
      const bN = b === '' || b == null ? null : b;
      if (Array.isArray(aN) && Array.isArray(bN)) {
        if (aN.length !== bN.length || aN.some((x, i) => x !== bN[i])) out.push(k);
        return;
      }
      if (String(aN) !== String(bN)) out.push(k);
    };
    compare('sku_name');
    compare('sku_code');
    compare('uom');
    compare('category');
    compare('supplier_id');
    compare('supplier_name');
    compare('unit_cost', v => v == null || v === '' ? null : Number(v));
    compare('list_price', v => v == null || v === '' ? null : Number(v));
    compare('lead_time_days', v => v == null || v === '' ? null : Number(v));
    compare('batch_size', v => v == null || v === '' ? null : Number(v));
    compare('reorder_point', v => v == null || v === '' ? null : Number(v));
    compare('barcode');
    compare('barcode_path');
    compare('notes');
    compare('tags');
    return out;
  }, [form, item]);

  const dirty = dirtyFields.length > 0;

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const update = {};
      for (const k of dirtyFields) {
        let v = form[k];
        if (['unit_cost', 'list_price'].includes(k)) {
          v = v === '' || v == null ? null : Number(v);
        }
        if (['lead_time_days', 'batch_size', 'reorder_point'].includes(k)) {
          v = v === '' || v == null ? null : parseInt(v);
        }
        if (k === 'tags') v = Array.isArray(v) ? v : [];
        update[k] = v;
      }
      // Price history: append a row when unit_cost changes.
      const oldCost = item.unit_cost == null ? null : Number(item.unit_cost);
      const newCost = update.unit_cost == null ? null : Number(update.unit_cost);
      const costChanged = dirtyFields.includes('unit_cost') && oldCost !== newCost;

      await authWrite(session, 'forecaster_catalog', 'update', update,
        { column: 'id', value: item.id });

      if (costChanged) {
        await authWrite(session, 'forecaster_price_history', 'insert', {
          catalog_id: item.id, old_cost: oldCost, new_cost: newCost, changed_by: session.name,
        });
      }
      // Sync sku_name onto the matching forecaster_skus row (only if changed).
      if (dirtyFields.includes('sku_name') && skuRow) {
        await authWrite(session, 'forecaster_skus', 'update',
          { sku_name: form.sku_name?.trim() }, { column: 'id', value: skuRow.id });
      }
      // Free-text activity mirror.
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name,
        action: 'Catalogue edit',
        detail: `${item.sku_name}: ${dirtyFields.join(', ')}`,
      }]);

      setJustSaved(true);
      onSaved?.();
      setTimeout(() => setJustSaved(false), 1400);
    } catch (err) {
      console.error('[CatalogueDrawer] save failed', err);
      alert('Save failed: ' + (err?.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(newStatus) {
    if (saving) return;
    setSaving(true);
    try {
      await authWrite(session, 'forecaster_catalog', 'update',
        { status: newStatus }, { column: 'id', value: item.id });
      if (skuRow) {
        await authWrite(session, 'forecaster_skus', 'update',
          { is_active: newStatus === 'active' }, { column: 'id', value: skuRow.id });
      }
      await aws.from('forecaster_activity').insert([{
        actor_name: session.name,
        action: newStatus === 'retired' ? 'Retired SKU' : 'Reactivated SKU',
        detail: item.sku_name,
      }]);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[CatalogueDrawer] status change failed', err);
      alert('Status change failed: ' + (err?.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function uploadBarcode(file) {
    if (!file) return;
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const key = `catalog/${item.id}/barcode/${Date.now()}-${safeName}`;
      const { error } = await storage.from('po-attachments').upload(key, file, { contentType: file.type });
      if (error) throw error;
      setForm(f => ({ ...f, barcode_path: key }));
    } catch (err) {
      console.error('[CatalogueDrawer] barcode upload failed', err);
      alert('Upload failed: ' + (err?.message || 'unknown error'));
    }
  }

  // Linked: open POs for this SKU.
  const linkedPOs = useMemo(() => {
    if (!skuRow) return [];
    return incomingData
      .filter(i => i.sku_id === skuRow.id && !i.received_at)
      .sort((a, b) => (a.expected_date || '').localeCompare(b.expected_date || ''));
  }, [incomingData, skuRow]);

  const skuPos = linkedPOs.length;

  return createPortal(
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(15,20,25,0.32)', backdropFilter: 'blur(2px)',
      }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(620px, 100vw)',
        background: '#FAFAF8', zIndex: 1000,
        display: 'flex', flexDirection: 'column', fontFamily: SANS,
        borderLeft: '1px solid #E8E6E0', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 26px 14px', borderBottom: '1px solid #E8E6E0',
          display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              fontFamily: SERIF, fontSize: 22, fontWeight: 400,
              color: '#0F1419', margin: 0, letterSpacing: '-0.01em',
            }}>{item.sku_name}</h2>
            <div style={{
              fontFamily: MONO, fontSize: 11, color: '#6E7278',
              marginTop: 4, display: 'flex', gap: 8, alignItems: 'center',
            }}>
              <span>{item.sku_code || '—'}</span>
              <span style={{ color: '#D5D3CD' }}>·</span>
              <span>{item.category || item.uom || '—'}</span>
              <span style={{ color: '#D5D3CD' }}>·</span>
              <span style={{
                color: item.status === 'retired' ? '#9A9DA1' : '#0F8E6F',
                fontWeight: 500,
              }}>{item.status === 'retired' ? 'Retired' : 'Active'}</span>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#6E7278', padding: 6, borderRadius: 6, lineHeight: 1,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Tab strip */}
        <div style={{
          display: 'flex', gap: 0, padding: '0 26px',
          borderBottom: '1px solid #E8E6E0', flexShrink: 0,
        }}>
          {[
            { id: 'details', label: 'Details' },
            { id: 'bom',     label: 'BoM' },
            { id: 'history', label: 'History' },
            { id: 'linked',  label: 'Linked', count: skuPos || null },
          ].map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '12px 0', marginRight: 22, marginBottom: -1,
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 12.5, color: active ? '#0F1419' : '#6E7278',
                fontWeight: active ? 500 : 400,
                borderBottom: active ? '2px solid #0F8E6F' : '2px solid transparent',
              }}>
                {t.label}
                {t.count != null && <span style={{ marginLeft: 5, fontFamily: MONO, fontSize: 10, color: '#9A9DA1' }}>{t.count}</span>}
              </button>
            );
          })}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 26px 26px' }}>
          {tab === 'details' && (
            <DetailsTab
              session={session}
              isAdmin={canCatalogueWrite}
              form={form}
              setForm={setForm}
              margin={margin}
              reorderSuggestion={reorderSuggestion}
              monthlyAvg={monthlyAvg}
              uploadBarcode={uploadBarcode}
              dirty={dirty}
              saving={saving}
              justSaved={justSaved}
              onSave={save}
              item={item}
              confirmRetire={confirmRetire}
              setConfirmRetire={setConfirmRetire}
              onRetire={() => setStatus('retired')}
              onReactivate={() => setStatus('active')}
            />
          )}

          {tab === 'bom' && <BomTab session={session} item={item} canEdit={canCatalogueWrite} />}

          {tab === 'history' && <HistoryTab item={item} />}

          {tab === 'linked' && <LinkedTab pos={linkedPOs} />}
        </div>
      </aside>
    </>,
    document.body,
  );
}

// ── Details tab ─────────────────────────────────────────────────────────
function DetailsTab({
  session, isAdmin, form, setForm, margin, reorderSuggestion, monthlyAvg,
  uploadBarcode, dirty, saving, justSaved, onSave, item,
  confirmRetire, setConfirmRetire, onRetire, onReactivate,
}) {
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!isAdmin) {
    return (
      <div style={{ ...emptyStyle, padding: '40px 0' }}>
        View-only. Catalogue edit permission required — ask an admin to enable it for your user.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Field label="Name">
        <input style={inputStyle} value={form.sku_name || ''} onChange={e => update('sku_name', e.target.value)} />
      </Field>
      <Row>
        <Field label="SKU code">
          <input style={inputStyle} value={form.sku_code || ''} onChange={e => update('sku_code', e.target.value)} />
        </Field>
        <Field label="UoM / Packaging">
          <select style={inputStyle} value={form.uom || ''} onChange={e => update('uom', e.target.value)}>
            {UOM_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Category (brand)">
        <select style={inputStyle} value={form.category || ''} onChange={e => update('category', e.target.value)}>
          {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c || '— Pick a brand —'}</option>)}
        </select>
      </Field>

      <Field label="Barcode / GTIN">
        <input style={inputStyle} value={form.barcode || ''} onChange={e => update('barcode', e.target.value)} placeholder="e.g. 9312345678901" />
      </Field>
      <Field label="Barcode file (image or PDF)">
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center',
          padding: '10px 12px', border: '1px dashed #E8E6E0', borderRadius: 8,
          background: 'rgba(15,20,25,0.025)',
        }}>
          {form.barcode_path ? (
            <>
              <span style={{ flex: 1, fontFamily: MONO, fontSize: 11, color: '#6E7278', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {form.barcode_path.split('/').pop()}
              </span>
              <button onClick={() => update('barcode_path', '')} style={ghostBtnStyle}>Remove</button>
            </>
          ) : (
            <span style={{ flex: 1, fontSize: 12, color: '#9A9DA1' }}>No file uploaded</span>
          )}
          <label style={{ ...ghostBtnStyle, cursor: 'pointer' }}>
            Upload
            <input type="file" hidden accept="image/*,.pdf" onChange={e => uploadBarcode(e.target?.files?.[0])} />
          </label>
        </div>
      </Field>

      <Field label="Supplier">
        <SupplierPicker
          session={session}
          value={{ id: form.supplier_id || null, name: form.supplier_name || '' }}
          onChange={({ id, name }) => {
            update('supplier_id', id);
            update('supplier_name', name);
          }}
        />
      </Field>

      <Row>
        <Field label="Lead time (days)">
          <input type="number" style={inputStyle} value={form.lead_time_days ?? ''} onChange={e => update('lead_time_days', e.target.value)} />
        </Field>
        <Field label="Batch / MOQ">
          <input type="number" style={inputStyle} value={form.batch_size ?? ''} onChange={e => update('batch_size', e.target.value)} />
        </Field>
        <Field label="Reorder point">
          <input
            type="number"
            style={inputStyle}
            value={form.reorder_point ?? ''}
            onChange={e => update('reorder_point', e.target.value)}
            placeholder={reorderSuggestion ? `Suggest ${reorderSuggestion}` : 'No sales data'}
          />
        </Field>
      </Row>
      {reorderSuggestion != null && (
        <div style={{ fontSize: 10.5, color: '#6E7278', marginTop: -10 }}>
          Suggested reorder point: <strong>{reorderSuggestion.toLocaleString()}</strong>
          {' — '}{Math.round(monthlyAvg / 30 * 10) / 10}/d × {form.lead_time_days || 84}d × 1.2 buffer.
          {form.reorder_point !== reorderSuggestion && (
            <button onClick={() => update('reorder_point', reorderSuggestion)} style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 5,
              border: '1px solid #E8E6E0', background: '#fff',
              fontSize: 10.5, cursor: 'pointer',
            }}>Use suggested</button>
          )}
        </div>
      )}

      <Row>
        <Field label="Unit cost">
          <input type="number" step="0.01" style={inputStyle} value={form.unit_cost ?? ''} onChange={e => update('unit_cost', e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="List price">
          <input type="number" step="0.01" style={inputStyle} value={form.list_price ?? ''} onChange={e => update('list_price', e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Margin">
          <div style={{ ...inputStyle, background: '#F4F2EC', display: 'flex', alignItems: 'center' }}>
            {margin != null ? `${margin}%` : '—'}
          </div>
        </Field>
      </Row>

      <Field label="Tags">
        <TagEditor tags={form.tags || []} onChange={(tags) => update('tags', tags)} />
      </Field>

      <Field label="Notes">
        <textarea
          rows={3}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
          value={form.notes || ''}
          onChange={e => update('notes', e.target.value)}
        />
      </Field>

      {/* Save */}
      <button
        disabled={!dirty || saving}
        onClick={onSave}
        style={{
          padding: '11px 16px', borderRadius: 9,
          background: justSaved ? '#0F8E6F'
            : dirty ? '#0F1419' : 'rgba(15,20,25,0.08)',
          color: justSaved || dirty ? '#fff' : '#9A9DA1',
          border: 'none', cursor: dirty && !saving ? 'pointer' : 'default',
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {justSaved ? 'Saved' : saving ? 'Saving…' : dirty ? `Save changes` : 'No changes'}
      </button>

      {/* Retire / Reactivate */}
      <div style={{
        marginTop: 8, paddingTop: 16,
        borderTop: '1px solid #E8E6E0',
      }}>
        {item.status === 'active' ? (
          confirmRetire ? (
            <div style={{
              padding: 12, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ fontSize: 13, color: '#92400E' }}>
                Retire <strong>{item.sku_name}</strong>? It will be hidden from On Hand and Catalogue active filters.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onRetire} disabled={saving} style={{
                  padding: '7px 12px', borderRadius: 7, border: 'none',
                  background: '#C2410C', color: '#fff', cursor: 'pointer',
                  fontSize: 12, fontWeight: 500,
                }}>Yes, retire</button>
                <button onClick={() => setConfirmRetire(false)} style={ghostBtnStyle}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setConfirmRetire(true)} style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #FCA5A5', background: '#fff', color: '#C2410C',
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
            }}>Retire SKU</button>
          )
        ) : (
          <button onClick={onReactivate} disabled={saving} style={{
            padding: '8px 14px', borderRadius: 8,
            border: '1px solid #BBF7D0', background: '#fff', color: '#0F8E6F',
            fontSize: 12, fontWeight: 500, cursor: 'pointer',
          }}>Reactivate SKU</button>
        )}
      </div>
    </div>
  );
}

function TagEditor({ tags, onChange }) {
  const [draft, setDraft] = useState('');
  function add(t) {
    const v = (t || draft).trim().toLowerCase();
    if (!v || tags.includes(v)) return;
    onChange([...tags, v]);
    setDraft('');
  }
  function remove(t) {
    onChange(tags.filter(x => x !== t));
  }
  return (
    <div style={{
      padding: '8px 10px', border: '1px solid #E8E6E0', borderRadius: 7,
      background: '#fff', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    }}>
      {tags.map(t => (
        <span key={t} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 8px', borderRadius: 999,
          background: '#0F1419', color: '#fff', fontSize: 11,
        }}>
          {t}
          <button onClick={() => remove(t)} style={{
            background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1,
          }}>×</button>
        </span>
      ))}
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        placeholder={tags.length === 0 ? 'Add tags…' : ''}
        style={{
          flex: 1, minWidth: 100, border: 'none', outline: 'none',
          fontSize: 12, color: '#0F1419', background: 'transparent',
        }}
      />
      {TAG_SUGGESTIONS.filter(s => !tags.includes(s)).map(s => (
        <button key={s} onClick={() => add(s)} style={{
          padding: '2px 8px', borderRadius: 999,
          border: '1px dashed #D5D3CD', background: 'transparent',
          fontSize: 10.5, color: '#6E7278', cursor: 'pointer',
        }}>+ {s}</button>
      ))}
    </div>
  );
}

// ── History tab ─────────────────────────────────────────────────────────
function HistoryTab({ item }) {
  const [priceHist, setPriceHist] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      const [phRes, actRes] = await Promise.all([
        aws.from('forecaster_price_history')
          .select('*').eq('catalog_id', item.id)
          .order('created_at', { ascending: false }).limit(50),
        aws.from('forecaster_activity')
          .select('*')
          .ilike('detail', `%${item.sku_name}%`)
          .order('created_at', { ascending: false }).limit(50),
      ]);
      if (!alive) return;
      setPriceHist(phRes.data || []);
      setActivity(actRes.data || []);
      setLoading(false);
    }
    load();
    return () => { alive = false; };
  }, [item.id, item.sku_name]);

  const merged = useMemo(() => {
    const a = priceHist.map(r => ({
      key: 'p-' + r.id,
      kind: 'price',
      ts: r.created_at,
      who: r.changed_by,
      oldCost: Number(r.old_cost || 0),
      newCost: Number(r.new_cost || 0),
    }));
    const b = activity.map(r => ({
      key: 'a-' + r.id,
      kind: 'activity',
      ts: r.created_at,
      who: r.actor_name,
      action: r.action,
      detail: r.detail,
    }));
    return [...a, ...b].sort((x, y) => (y.ts || '').localeCompare(x.ts || ''));
  }, [priceHist, activity]);

  if (loading) return <div style={emptyStyle}>Loading history…</div>;
  if (merged.length === 0) return <div style={emptyStyle}>No history yet.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {merged.map(r => (
        <div key={r.key} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
          <div style={{
            width: 8, height: 8, borderRadius: 999,
            background: r.kind === 'price' ? '#0F8E6F' : '#9A9DA1',
            marginTop: 5, flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: '#0F1419' }}>
              {r.kind === 'price' ? (
                <>
                  <strong style={{ fontWeight: 500 }}>Unit cost</strong>
                  {' · '}
                  <span style={{ fontFamily: MONO }}>
                    {fmtMoney(r.oldCost)} → {fmtMoney(r.newCost)}
                  </span>
                  <span style={{
                    color: r.newCost > r.oldCost ? '#C2410C' : '#0F8E6F',
                    marginLeft: 6, fontSize: 11,
                  }}>
                    {r.newCost > r.oldCost ? '↑' : '↓'}{fmtMoney(Math.abs(r.newCost - r.oldCost))}
                  </span>
                </>
              ) : (
                <>{r.action}{r.detail && <span style={{ color: '#6E7278' }}> · {r.detail}</span>}</>
              )}
            </div>
            <div style={{
              fontFamily: MONO, fontSize: 10, color: '#9A9DA1',
              letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2,
            }}>
              {fmtRelative(r.ts)} · {r.who || 'system'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Linked tab ──────────────────────────────────────────────────────────
function LinkedTab({ pos }) {
  if (!pos.length) {
    return <div style={emptyStyle}>No open POs for this SKU.</div>;
  }
  return (
    <div>
      <div style={{ ...labelStyle, marginBottom: 10 }}>Open POs · {pos.length}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pos.map(po => {
          const etaMs = po.expected_date ? new Date(po.expected_date).getTime() - Date.now() : null;
          const etaDays = etaMs != null ? Math.ceil(etaMs / 86400000) : null;
          return (
            <div key={po.id} style={{
              padding: '12px 14px', background: '#fff',
              border: '1px solid #E8E6E0', borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: '#6E7278' }}>{po.po_ref || '—'}</div>
                <div style={{ fontSize: 13, color: '#0F1419', marginTop: 3 }}>
                  {Number(po.qty || 0).toLocaleString()} units
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, color: '#0F1419' }}>{fmtShortDate(po.expected_date)}</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: etaDays != null && etaDays < 0 ? '#C2410C' : '#9A9DA1', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  {etaDays == null ? '—' : etaDays < 0 ? `${Math.abs(etaDays)}d late` : etaDays === 0 ? 'today' : `in ${etaDays}d`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}
function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Array.isArray(children) ? children.length : 1}, 1fr)`, gap: 12 }}>{children}</div>;
}

const labelStyle = {
  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
  textTransform: 'uppercase', color: '#6E7278', fontWeight: 500,
};
const inputStyle = {
  padding: '8px 12px',
  border: '1px solid #E8E6E0', borderRadius: 7,
  background: '#fff', fontSize: 13, color: '#0F1419',
  fontFamily: 'inherit', outline: 'none', width: '100%',
  boxSizing: 'border-box',
};
const ghostBtnStyle = {
  padding: '6px 10px', borderRadius: 6,
  border: '1px solid #E8E6E0', background: '#fff',
  fontSize: 11, color: '#0F1419', cursor: 'pointer',
};
const emptyStyle = {
  color: '#9A9DA1', fontSize: 13, padding: '40px 0', textAlign: 'center',
};

// ── BoM tab ────────────────────────────────────────────────────────
// Non-formulation Bill of Materials (Ace's "what does each parent SKU
// consume?" point). Per parent SKU: a small table of (component, qty,
// notes) — components are picked from the catalogue. Future-proofs
// version transitions: counting how many sachet films / boxes / labels
// will be needed for a 12-month run of v4.7 becomes a query.
function BomTab({ session, item, canEdit }) {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [adding, setAdding] = useState(null); // { component_catalog_id, qty_per_unit, notes }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: bom }, { data: cat }] = await Promise.all([
      aws.from('forecaster_catalog_bom')
        .select('id, parent_catalog_id, component_catalog_id, qty_per_unit, notes, created_at, created_by')
        .eq('parent_catalog_id', item.id)
        .order('created_at', { ascending: true }),
      aws.from('forecaster_catalog')
        .select('id, sku_name, sku_code, status')
        .eq('status', 'active')
        .order('sku_name'),
    ]);
    setRows(bom || []);
    setCatalog(cat || []);
    setLoading(false);
  }, [item.id]);

  useEffect(() => { load(); }, [load]);

  const lookup = useMemo(() => {
    const m = {};
    for (const c of catalog) m[c.id] = c;
    return m;
  }, [catalog]);

  // Components Mo can pick from — exclude the parent (CHECK constraint
  // forbids self-loops) and anything already a component on this SKU.
  const usedIds = new Set(rows.map(r => r.component_catalog_id));
  const candidates = catalog.filter(c => c.id !== item.id && !usedIds.has(c.id));

  async function addRow() {
    if (!adding?.component_catalog_id) return;
    const qty = parseFloat(adding.qty_per_unit);
    if (!Number.isFinite(qty) || qty <= 0) return;
    try {
      await authWrite(session, 'forecaster_catalog_bom', 'insert', {
        parent_catalog_id: item.id,
        component_catalog_id: adding.component_catalog_id,
        qty_per_unit: qty,
        notes: adding.notes?.trim() || null,
        created_by: session?.code || null,
      });
      setAdding(null);
      await load();
    } catch (e) {
      alert('Could not add: ' + (e.message || 'unknown error'));
    }
  }

  async function deleteRow(id) {
    if (!confirm('Remove this component from the BoM?')) return;
    try {
      await authWrite(session, 'forecaster_catalog_bom', 'delete', null, { column: 'id', value: id });
      await load();
    } catch (e) {
      alert('Could not delete: ' + (e.message || 'unknown error'));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 12, color: '#6E7278', lineHeight: 1.5 }}>
        Non-formulation BoM. List the catalogue items this SKU consumes per
        unit — sachet film, outer box, labels, caps, etc. Used for transition
        planning when versions change.
      </div>

      {loading ? (
        <div style={emptyStyle}>Loading…</div>
      ) : rows.length === 0 && !adding ? (
        <div style={{ ...emptyStyle, padding: '24px 0' }}>No components on this BoM yet.</div>
      ) : (
        <div style={{ border: '1px solid #E8E6E0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#F6F3EC' }}>
                <th style={bomTh}>Component</th>
                <th style={{ ...bomTh, width: 90, textAlign: 'right' }}>Qty/unit</th>
                <th style={bomTh}>Notes</th>
                {canEdit && <th style={{ ...bomTh, width: 30 }}></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const c = lookup[r.component_catalog_id];
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #E8E6E0' }}>
                    <td style={bomTd}>
                      <div style={{ fontWeight: 500 }}>{c?.sku_name || '(deleted)'}</div>
                      {c?.sku_code && (
                        <div style={{ fontFamily: MONO, fontSize: 10, color: '#9A9DA1' }}>{c.sku_code}</div>
                      )}
                    </td>
                    <td style={{ ...bomTd, textAlign: 'right', fontFamily: MONO }}>
                      {Number(r.qty_per_unit).toLocaleString('en-AU', { maximumFractionDigits: 4 })}
                    </td>
                    <td style={{ ...bomTd, color: '#52575C' }}>{r.notes || '—'}</td>
                    {canEdit && (
                      <td style={bomTd}>
                        <button
                          onClick={() => deleteRow(r.id)}
                          aria-label="Remove component"
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: '#A8ADB1', fontSize: 14, padding: 4,
                          }}
                        >×</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && adding && (
        <div style={{
          background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 8,
          padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#6E7278', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Add component</div>
          <select
            style={inputStyle}
            value={adding.component_catalog_id || ''}
            onChange={e => setAdding(a => ({ ...a, component_catalog_id: e.target.value }))}
          >
            <option value="">— Pick a SKU —</option>
            {candidates.map(c => (
              <option key={c.id} value={c.id}>{c.sku_code ? `${c.sku_code} — ${c.sku_name}` : c.sku_name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 0.4 }}
              type="number" step="0.0001" min="0"
              placeholder="Qty / unit"
              value={adding.qty_per_unit || ''}
              onChange={e => setAdding(a => ({ ...a, qty_per_unit: e.target.value }))}
            />
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Notes (optional)"
              value={adding.notes || ''}
              onChange={e => setAdding(a => ({ ...a, notes: e.target.value }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(null)} style={ghostBtnStyle}>Cancel</button>
            <button
              onClick={addRow}
              disabled={!adding.component_catalog_id || !adding.qty_per_unit}
              style={{
                ...ghostBtnStyle,
                background: adding.component_catalog_id && adding.qty_per_unit ? '#0F1419' : '#A8ADB1',
                color: '#F4EFE6', borderColor: '#0F1419',
              }}
            >Add</button>
          </div>
        </div>
      )}

      {canEdit && !adding && (
        <button
          onClick={() => setAdding({ component_catalog_id: '', qty_per_unit: '', notes: '' })}
          style={{
            ...ghostBtnStyle, alignSelf: 'flex-start', padding: '7px 14px',
          }}
        >+ Add component</button>
      )}
    </div>
  );
}

const bomTh = {
  fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: '#6E7278', fontWeight: 500,
  textAlign: 'left', padding: '8px 12px',
};
const bomTd = {
  padding: '10px 12px', verticalAlign: 'top', color: '#1F262C',
};
